import Icon from '@components/dataDisplay/Icons/Icon';
import React, { CSSProperties, useMemo } from 'react';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { homeSettingState } from '@atom/setting/homeSettingState';
import { getArrowButtonComponent } from '@components/dataEntry/button/BackBtn/designs';

// ─────────────────────────────────────────────
// 기존 기본 스타일 (디자인 미설정 시 fallback)
// ─────────────────────────────────────────────
const ArrowButton = styled.button<{
  direction: 'left' | 'right';
  size: number;
}>`
  overflow: hidden;
  position: absolute;
  display: flex;
  flex-direction: ${({ direction }) =>
    direction === 'left' ? 'row' : 'row-reverse'};
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--lineColor);
  background-color: rgba(0, 0, 0, 0.1);
  opacity: 0.8;
  padding: 16px;
  font-size: 16px;
  font-weight: 700;
  border-radius: 50px;
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};

  &:hover {
    background: var(--backgroundColor);
  }

  .Arrow1,
  .Arrow2 {
    position: absolute;
    width: ${({ size }) => `${size}px`};
    height: ${({ size }) => `${size}px`};

    svg {
      width: ${({ size }) => `${size}px`};
      height: ${({ size }) => `${size}px`};
    }
  }

  .Arrow1 {
    transition: all 0.3s 0.2s;
    transform: translateX(0);
  }
  .Arrow2 {
    top: 50%;
    bottom: 0;
    transition: all 0.4s;
    transform: translate(
      ${({ direction }) => (direction === 'right' ? '-20px' : '20px')},
      -50%
    );
    opacity: 0;
  }

  &:hover {
    opacity: 1;
    .Arrow1 {
      transform: translateX(
        ${({ direction }) => (direction === 'right' ? '20px' : '-20px')}
      );
      transition-delay: 0s;
      opacity: 0;
    }

    .Arrow2 {
      transform: translate(0, -50%);
      opacity: 1;
    }
  }
`;

// ─────────────────────────────────────────────
// 디자인 시스템 적용 시 래퍼
// ─────────────────────────────────────────────
const DesignArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

interface Props {
  direction?: 'left' | 'right';
  onClick?: (e?) => void;
  children?: JSX.Element | string;
  size?: number;
  style?: CSSProperties;
}

function ArrowBtn({
  direction = 'left',
  onClick,
  children,
  size = 20,
  style,
}: Props) {
  const homeSetting = useRecoilValue(homeSettingState);
  const arrowBtnConfig = homeSetting?.arrowButton;
  const designType = arrowBtnConfig?.design;

  const DesignComponent = useMemo(
    () => (designType ? getArrowButtonComponent(designType) : null),
    [designType],
  );

  // 관리자가 화살표 디자인을 설정한 경우 → 디자인 시스템 사용
  if (DesignComponent) {
    return (
      <DesignArrowButton
        onClick={onClick}
        style={style}
        data-sound-type="default"
      >
        <DesignComponent
          color={arrowBtnConfig?.color}
          backgroundColor={arrowBtnConfig?.backgroundColor}
          pointColor={arrowBtnConfig?.pointColor}
          direction={direction}
        />
        {children && <span>{children}</span>}
      </DesignArrowButton>
    );
  }

  // 디자인 미설정 시 → 기존 기본 ArrowBtn 유지
  const drawArrow = {
    left: (
      <Icon
        name="LeftArrow"
        style={{ width: `${size}px`, height: `${size}px` }}
        color="var(--textColor)"
      />
    ),
    right: (
      <Icon
        name="RightArrow"
        style={{ width: `${size}px`, height: `${size}px` }}
        color="var(--textColor)"
      />
    ),
  };

  return (
    <ArrowButton
      direction={direction}
      onClick={onClick}
      size={size}
      style={style}
      data-sound-type="default"
    >
      <div className="Arrow1">{drawArrow[direction]}</div>
      <div className="Arrow2">{drawArrow[direction]}</div>
      <span>{children}</span>
    </ArrowButton>
  );
}

export default ArrowBtn;
